import React, { useState, useEffect, useRef, useCallback } from 'react';
import { formatCurrency, parsePriceValue } from '../utils/cart';

const API_BASE = process.env.REACT_APP_API_URL || '';
const PAGE_SIZE = 20;

// Map a free-form status from the sheet to a badge style.
function statusClass(status) {
  const s = (status || '').toLowerCase();
  if (/(cancel|reject)/.test(s)) return 'cancelled';
  if (/(collect|complete|deliver|done|fulfil)/.test(s)) return 'done';
  if (/(ready|pickup|payment received|payment|sent)/.test(s)) return 'ready';
  if (/(pending|submit|process)/.test(s)) return 'pending';
  if (/(fail|decline|error)/.test(s)) return 'cancelled';
  return 'neutral';
}

// Badge for Payment Mode — shows COD / Razorpay / Free unobtrusively
function PaymentModeBadge({ mode }) {
  if (!mode || mode === 'COD') return null;
  const label = mode === 'Free' ? 'Free' : 'Razorpay';
  return <span className={`oh-payment-badge oh-payment-${label.toLowerCase()}`}>{label}</span>;
}

const num = (v) => parsePriceValue(v) || 0;
const isCancelled = (s) => /(cancel|reject)/.test((s || '').toLowerCase());

const PinIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

function OrderCard({ order }) {
  const grand = order.items.reduce((t, it) => t + num(it.itemTotalIncl), 0);
  const subExcl = order.items.reduce((t, it) => t + num(it.itemTotalExcl), 0);
  const gstAmt = Math.max(0, grand - subExcl);
  const refunded = order.items.reduce((t, it) => t + num(it.refundAmount), 0);
  const net = grand - refunded;

  // If all items are Pending and payment is Razorpay, show a softer "awaiting payment" note
  const allPending = order.items.every(it => (it.status || '').toLowerCase() === 'pending');
  const isRazorpay = order.paymentMode === 'Razorpay';

  return (
    <div className="oh-card">
      <div className="oh-card-head">
        <span className="oh-head-id">
          <span className="oh-label">Order id:</span> <span className="oh-id">{order.orderId || '—'}</span>
          <PaymentModeBadge mode={order.paymentMode} />
        </span>
        <span className="oh-meta">
          <span className="oh-label">Placed on:</span> {order.timestamp || '—'} · {order.items.length} item{order.items.length !== 1 ? 's' : ''}
        </span>
      </div>

      {isRazorpay && allPending && (
        <div className="oh-payment-note oh-payment-pending-note">
          Payment pending — this order will be confirmed once payment is received.
        </div>
      )}

      <div className="oh-grid oh-head-row">
        <span></span><span>Item</span>
        <span className="oh-c">Qty</span>
        <span className="oh-r">Price/Qty</span>
        <span className="oh-r">GST%</span>
        <span className="oh-r">Total</span>
        <span className="oh-r">Status</span>
      </div>

      {order.items.map((item, i) => {
        const refundAmt = num(item.refundAmount);
        const greyed = isCancelled(item.status) || refundAmt > 0;
        return (
          <div className={`oh-grid oh-item ${greyed ? 'greyed' : ''}`} key={`${item.sku}-${i}`}>
            <span className="oh-num">{i + 1}.</span>
            <span className="oh-name-cell">
              <span className="oh-name">{item.description}</span>
              <span className="oh-sku">
                {item.sku}
                {item.customization === 'yes' && (
                  item.driveLink
                    ? <a className="oh-file" href={item.driveLink} target="_blank" rel="noreferrer">file</a>
                    : <span className="oh-tag">custom</span>
                )}
              </span>
              {refundAmt > 0 && (
                <span className="oh-refund">↩ Refunded {formatCurrency(refundAmt)}{item.refundReason ? ` · ${item.refundReason}` : ''}</span>
              )}
            </span>
            <span className="oh-c oh-strong">{item.quantity || 1}</span>
            <span className="oh-r oh-strong">{item.unitPrice ? formatCurrency(num(item.unitPrice)) : '—'}</span>
            <span className="oh-r oh-muted">{item.gst ? `${num(item.gst)}%` : '—'}</span>
            <span className="oh-r oh-strong">{item.itemTotalIncl ? formatCurrency(num(item.itemTotalIncl)) : '—'}</span>
            <span className="oh-r"><span className={`order-status ${statusClass(item.status)}`}>{item.status}</span></span>
          </div>
        );
      })}

      <div className="oh-card-foot">
        <span className="oh-pickup">
          <PinIcon /> {order.pickupStore ? `${order.pickupStore}${order.pickupCity ? ` · ${order.pickupCity}` : ''}` : 'Pickup not set'}
        </span>
        <div className="oh-totals">
          <div>
            Subtotal <b>{formatCurrency(subExcl)}</b> · GST <b>{formatCurrency(gstAmt)}</b> · Grand Total <span className="oh-tiny">(incl GST)</span> <b className="oh-grand">{formatCurrency(grand)}</b>
          </div>
          {refunded > 0 && (
            <div className="oh-refundline">
              Refunded <b className="oh-refund-amt">{formatCurrency(refunded)}</b> · Net paid <b className="oh-grand">{formatCurrency(net)}</b>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function OrderHistory({ token }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(`${API_BASE}/api/my-orders`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Could not load your orders');
        if (!cancelled) setOrders(data.orders || []);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const loadMore = useCallback(() => {
    setVisibleCount(prev => Math.min(prev + PAGE_SIZE, orders.length));
  }, [orders.length]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMore(); },
      { rootMargin: '300px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore, visibleCount]);

  const visible = orders.slice(0, visibleCount);
  const hasMore = visibleCount < orders.length;

  return (
    <div className="orders-page">
      <div className="orders-main">
        {error && <div className="error-message">{error}</div>}

        <div className="section-header">
          <span className="section-count">{orders.length} order{orders.length !== 1 ? 's' : ''}</span>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading your orders…</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="empty-state">
            <h3>No orders yet</h3>
            <p>Your placed orders and their status will appear here.</p>
          </div>
        ) : (
          <div className="oh-list">
            {visible.map((order, idx) => (
              <OrderCard key={`${order.orderId || order.timestamp}-${idx}`} order={order} />
            ))}

            {hasMore && (
              <div ref={sentinelRef} className="load-more-sentinel">
                <div className="loading-spinner"></div>
              </div>
            )}
            {!hasMore && orders.length > PAGE_SIZE && (
              <p className="all-loaded">All {orders.length} orders loaded</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default OrderHistory;
